import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/agent',         icon: '💬', label: 'Live Chats' },
  { to: '/agent/leads',   icon: '👥', label: 'Leads' },
  { to: '/agent/crm',     icon: '📋', label: 'CRM' },
  { to: '/agent/profile', icon: '👤', label: 'My Profile' },
];

export default function AgentDashboard() {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [historyChats, setHistoryChats] = useState([]);
  const [offlineChats, setOfflineChats] = useState([]);
  const [selected, setSelected] = useState(null);
  const firstLoadDone = React.useRef(false);
  const selectedRef = React.useRef(null);

  // Keep selectedRef in sync with selected state
  const setSelectedWithRef = (chat) => {
    selectedRef.current = chat;
    setSelected(chat);
    if (chat) localStorage.setItem('agent_selected_chat', chat.id);
    else localStorage.removeItem('agent_selected_chat');
  };
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState('');
  const [status, setStatus] = useState('offline');
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [socket, setSocket] = useState(null);
  const [cannedReplies, setCannedReplies] = useState([]);
  const [pinnedChats, setPinnedChats] = useState(() => {
    try { return JSON.parse(localStorage.getItem('agent_pinned_chats') || '[]'); } catch { return []; }
  });
  const [now, setNow] = useState(Date.now());
  const [showCanned, setShowCanned] = useState(false);
  const [tab, setTab] = useState('live');
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [prechatAnswers, setPrechatAnswers] = useState([]);
  const [showPrechat, setShowPrechat] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);

  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const msgEnd = useRef(null);
  const isMainAgent = user?.role === 'main_agent';

  const playNotif = (type = 'message') => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      if (type === 'new_chat') {
        // Two-tone for new chat
        osc.frequency.setValueAtTime(600, ctx.currentTime);
        osc.frequency.setValueAtTime(900, ctx.currentTime + 0.15);
        osc.frequency.setValueAtTime(600, ctx.currentTime + 0.3);
        gain.gain.setValueAtTime(0.4, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.5);
      } else {
        // Single tone for message
        osc.frequency.setValueAtTime(800, ctx.currentTime);
        osc.frequency.setValueAtTime(600, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.25);
      }
    } catch(e) {}
  };

  useEffect(() => {
    // Wait for user to be loaded from AuthContext
    if (!user) return;
    // Restore previous status (so refresh doesn't reset to offline)
    const savedStatus = localStorage.getItem('agent_status') || 'online';
    setStatus(savedStatus);
    // Restore status in backend on page load
    axios.put('/api/agents/status/update', { status: savedStatus }).catch(() => {});

    const s = io('https://api.mschat.msdiscoveries.com');
    setSocket(s);
    s.emit('agent:join', { agent_id: user.id, business_id: user.business_id });
    s.on('new:chat', () => {
      playNotif('new_chat');
      toast('New chat incoming!', { icon: '💬', duration: 4000 });
      // Reload chats and auto-open if none selected
      axios.get('/api/chats?status=open&limit=50').then(r => {
        const loaded = r.data.data || [];
        setChats(loaded);
        // Auto open the new chat if agent has no chat open currently
        if (!selectedRef.current && loaded.length > 0) {
          const latest = loaded[0];
          setSelectedWithRef(latest);
          loadMessages(latest.id);
        }
      }).catch(() => {});
    });
    // Listen for client ending chat
    s.on('chat:resolved', (data) => {
      const chatId = data.chat_id;
      playNotif('new_chat');
      // Remove from live chats
      setChats(prev => prev.filter(c => c.id !== chatId));
      // If this is the currently open chat
      setSelected(prev => {
        if (prev?.id === chatId) {
          const visitorName = prev.visitor_name || 'Visitor';
          // Show banner with name
          toast(`⚠️ ${visitorName} has ended the chat`, {
            duration: 6000,
            style: { background: '#1a1000', border: '1px solid #f59e0b', color: '#f59e0b', fontWeight: 600 }
          });
          // Open rating modal so agent can rate
          setTimeout(() => setShowEndModal(true), 500);
          return { ...prev, status: 'resolved' };
        } else {
          // Chat not currently open - just show toast
          toast(`⚠️ A visitor has ended the chat`, {
            duration: 4000,
            style: { background: '#1a1000', border: '1px solid #f59e0b', color: '#f59e0b' }
          });
        }
        return prev;
      });
      loadHistory();
    });

    s.on('message:received', (data) => {
      if (data.sender_type !== 'agent') {
        setMessages(prev => [...prev, data]);
        setTimeout(() => msgEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        playNotif();
        setChats(prev => prev.map(c =>
          c.id === data.chat_id
            ? { ...c, unread_count: (c.unread_count || 0) + 1, last_message: data.message_text }
            : c
        ));
      }
    });
    loadChats();
    loadHistory();
    loadOfflineChats();
    axios.get('/api/business/canned-replies').then(r => setCannedReplies(r.data.data || [])).catch(() => {});

    // Update waiting timers every 30 seconds
    const timerInterval = setInterval(() => setNow(Date.now()), 30000);

    // Cleanup on unmount
    return () => {
      s.disconnect();
      clearInterval(timerInterval);
    };
  }, [user?.id]); // Re-run when user loads

  const loadMessages = (chatId) => {
    axios.get(`/api/chats/${chatId}/messages`).then(r => {
      setMessages(r.data.data || []);
      setTimeout(() => msgEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }).catch(() => {});
  };

  const restoreChat = async (loaded) => {
    const savedId = localStorage.getItem('agent_selected_chat');
    if (savedId) {
      // First try to find in loaded list
      const found = loaded.find(c => String(c.id) === String(savedId));
      if (found) {
        setSelectedWithRef(found);
        loadMessages(found.id);
        axios.get(`/api/business/prechat-answers/${found.id}`).then(r2 => setPrechatAnswers(r2.data.data || [])).catch(() => {});
        return;
      }
      // Not in list - fetch directly from API (maybe it's resolved/active)
      try {
        const r = await axios.get(`/api/chats/${savedId}/messages`);
        if (r.data.success) {
          // Get chat details
          const allChats = await axios.get(`/api/chats?limit=200`);
          const chatData = (allChats.data.data || []).find(c => String(c.id) === String(savedId));
          if (chatData) {
            setSelectedWithRef(chatData);
            setMessages(r.data.data || []);
            axios.get(`/api/business/prechat-answers/${savedId}`).then(r2 => setPrechatAnswers(r2.data.data || [])).catch(() => {});
            return;
          }
        }
      } catch(e) {}
    }
    // No saved chat - auto-open latest
    if (loaded.length > 0) {
      const latest = loaded[0];
      setSelectedWithRef(latest);
      loadMessages(latest.id);
      axios.get(`/api/business/prechat-answers/${latest.id}`).then(r2 => setPrechatAnswers(r2.data.data || [])).catch(() => {});
    }
  };

  const loadChats = () => {
    axios.get('/api/chats?status=open&limit=50').then(r => {
      const loaded = r.data.data || [];
      setChats(loaded);
      // Only restore on first load
      if (!firstLoadDone.current) {
        firstLoadDone.current = true;
        restoreChat(loaded);
      }
    }).catch(() => {});
  };

  const loadOfflineChats = () => {
    axios.get('/api/chats?status=missed&limit=100').then(r => setOfflineChats(r.data.data || [])).catch(() => {});
  };

  const loadHistory = () => {
    // Load both resolved and missed chats for history
    axios.get('/api/chats?status=resolved&limit=200').then(r => {
      setHistoryChats(r.data.data || []);
    }).catch(() => {});
  };

  const togglePin = (chatId, e) => {
    e.stopPropagation();
    const updated = pinnedChats.includes(chatId)
      ? pinnedChats.filter(id => id !== chatId)
      : [...pinnedChats, chatId];
    setPinnedChats(updated);
    localStorage.setItem('agent_pinned_chats', JSON.stringify(updated));
  };

  const getWaitingTime = (startedAt) => {
    if (!startedAt) return '';
    const mins = Math.floor((now - new Date(startedAt)) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return mins + 'm waiting';
    const hrs = Math.floor(mins / 60);
    return hrs + 'h ' + (mins % 60) + 'm waiting';
  };

  const selectChat = (chat) => {
    setSelectedWithRef(chat);
    setRating(0);
    setRatingComment('');
    setPrechatAnswers([]);
    setShowPrechat(false);
    loadMessages(chat.id);
    // Load answers immediately and retry after 2s (answers saved async after chat init)
    axios.get(`/api/business/prechat-answers/${chat.id}`).then(r => {
      const data = r.data.data || [];
      setPrechatAnswers(data);
      // Retry after 2s in case answers weren't saved yet
      if (data.length === 0) {
        setTimeout(() => {
          axios.get(`/api/business/prechat-answers/${chat.id}`).then(r2 => setPrechatAnswers(r2.data.data || [])).catch(() => {});
        }, 2000);
      }
    }).catch(() => {});
    if (socket && chat.status === 'open') {
      socket.emit('agent:accept', { chat_id: chat.id, agent_id: user.id });
    }
  };

  const sendMsg = async () => {
    if (!msg.trim() || !selected) return;
    const data = {
      chat_id: selected.id, message_text: msg,
      sender_type: 'agent', sender_id: user.id,
      sender_name: user.name, sent_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, data]);
    await axios.post('/api/chats/message', data).catch(() => {});
    socket?.emit('message:send', data);
    setMsg('');
    setTimeout(() => msgEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleEndChat = async () => {
    if (!selected) return;
    if (rating > 0) {
      await axios.post(`/api/chats/${selected.id}/rate`, { rating, comment: ratingComment }).catch(() => {});
    }
    await axios.put(`/api/chats/${selected.id}/resolve`).catch(() => {});
    socket?.emit('chat:resolve', { chat_id: selected.id, business_id: user.business_id });
    toast.success('Chat ended successfully');
    setShowEndModal(false);
    setSelectedWithRef(null);
    setMessages([]);
    setRating(0);
    setRatingComment('');
    loadChats();
    loadHistory();
    loadOfflineChats();
  };



  const addNote = async () => {
    if (!note.trim()) return;
    await axios.post(`/api/chats/${selected.id}/notes`, { note_text: note });
    toast.success('Note saved');
    setNote('');
    setShowNote(false);
  };

  const updateStatus = async (s) => {
    setStatus(s);
    await axios.put('/api/agents/status/update', { status: s });
    socket?.emit('agent:status', { agent_id: user.id, business_id: user.business_id, status: s });
    toast.success(`You are now ${s}`);
  };

  const sortedChats = [...chats].sort((a, b) => {
    const aPinned = pinnedChats.includes(a.id) ? 1 : 0;
    const bPinned = pinnedChats.includes(b.id) ? 1 : 0;
    return bPinned - aPinned;
  });
  const displayChats = tab === 'live' ? sortedChats : tab === 'offline' ? offlineChats : historyChats;

  return (
    <DashLayout navItems={nav} role={isMainAgent ? 'Main Agent' : 'Agent'}>
      <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

        {/* End Chat Modal */}



        {showEndModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>End Chat</h3>
              <p style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>Rate this conversation before ending (optional)</p>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Conversation rating</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <div key={star} onClick={() => setRating(star)}
                      style={{ fontSize: 28, cursor: 'pointer', color: star <= rating ? '#f59e0b' : '#333', transition: 'color 0.15s' }}>★</div>
                  ))}
                </div>
                {rating > 0 && (
                  <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 4 }}>
                    {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][rating]}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Add a note (optional)</div>
                <textarea value={ratingComment} onChange={e => setRatingComment(e.target.value)}
                  placeholder="How did the conversation go?" rows={3}
                  style={{ resize: 'none', width: '100%', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', padding: '8px 12px', borderRadius: 8, fontFamily: 'inherit', fontSize: 13 }} />
              </div>
              <div style={{ background: '#0d0d0d', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: '#555' }}>
                <div>Visitor: <span style={{ color: '#fff' }}>{selected?.visitor_name || 'Unknown'}</span></div>
                <div>Email: <span style={{ color: '#888' }}>{selected?.visitor_email || '—'}</span></div>
                <div>Duration: <span style={{ color: '#888' }}>{selected?.started_at ? Math.round((new Date() - new Date(selected.started_at)) / 60000) + ' min' : '—'}</span></div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowEndModal(false)}
                  style={{ flex: 1, padding: '11px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button onClick={handleEndChat}
                  style={{ flex: 2, padding: '11px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                  {rating > 0 ? `End Chat (${rating}★)` : 'End Chat'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Left sidebar */}
        <div style={{ width: 290, borderRight: '1px solid #1e1e1e', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #1e1e1e' }}>
            {/* Status buttons */}
            <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
              {['online', 'offline', 'away'].map(s => (
                <button key={s} onClick={() => updateStatus(s)}
                  style={{ flex: 1, padding: '5px 4px', borderRadius: 6, border: `1px solid ${status === s ? '#00d55b' : '#1e1e1e'}`, background: status === s ? 'rgba(0,213,91,0.1)' : 'transparent', color: status === s ? '#00d55b' : '#555', fontSize: 11, fontWeight: status === s ? 600 : 400, cursor: 'pointer', textTransform: 'capitalize' }}>
                  {s}
                </button>
              ))}
            </div>
            {/* 3 Tabs */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5 }}>
              <button onClick={() => setTab('live')}
                style={{ padding: '6px 4px', borderRadius: 6, border: `1px solid ${tab === 'live' ? '#00d55b' : '#1e1e1e'}`, background: tab === 'live' ? 'rgba(0,213,91,0.1)' : 'transparent', color: tab === 'live' ? '#00d55b' : '#555', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                💬 Live
                {chats.length > 0 && <span style={{ background: chats.some(c => c.status === 'pending') ? '#f59e0b' : '#00d55b', color: '#000', fontSize: 9, padding: '0 5px', borderRadius: 8, fontWeight: 700 }}>{chats.length}</span>}
              </button>
              <button onClick={() => { setTab('offline'); loadOfflineChats(); }}
                style={{ padding: '6px 4px', borderRadius: 6, border: `1px solid ${tab === 'offline' ? '#f59e0b' : '#1e1e1e'}`, background: tab === 'offline' ? 'rgba(245,158,11,0.1)' : 'transparent', color: tab === 'offline' ? '#f59e0b' : '#555', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                📴 Offline
                {offlineChats.length > 0 && <span style={{ background: '#f59e0b', color: '#000', fontSize: 9, padding: '0 5px', borderRadius: 8, fontWeight: 700 }}>{offlineChats.length}</span>}
              </button>
              <button onClick={() => { setTab('history'); loadHistory(); }}
                style={{ padding: '6px 4px', borderRadius: 6, border: `1px solid ${tab === 'history' ? '#00d55b' : '#1e1e1e'}`, background: tab === 'history' ? 'rgba(0,213,91,0.1)' : 'transparent', color: tab === 'history' ? '#00d55b' : '#555', fontSize: 11, cursor: 'pointer' }}>
                📋 History
              </button>
            </div>
          </div>

          {/* Chat list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {displayChats.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#333', fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{tab === 'live' ? '💬' : tab === 'offline' ? '📴' : '📋'}</div>
                {tab === 'live' ? 'No active chats. Go online to receive chats.' : tab === 'offline' ? 'No offline messages.' : 'No chat history yet.'}
              </div>
            ) : displayChats.map(chat => (
              <div key={chat.id} onClick={() => selectChat(chat)}
                style={{ padding: '11px 14px', borderBottom: '1px solid #0d0d0d', cursor: 'pointer', background: selected?.id === chat.id ? (tab === 'offline' ? 'rgba(245,158,11,0.04)' : 'rgba(0,213,91,0.04)') : 'transparent', borderLeft: `3px solid ${selected?.id === chat.id ? (tab === 'offline' ? '#f59e0b' : '#00d55b') : 'transparent'}` }}
                onMouseEnter={e => { if (selected?.id !== chat.id) e.currentTarget.style.background = '#0d0d0d'; }}
                onMouseLeave={e => { if (selected?.id !== chat.id) e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: tab === 'offline' ? '#1a0d00' : tab === 'live' ? '#001a0d' : '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: tab === 'offline' ? '#f59e0b' : tab === 'live' ? '#00d55b' : '#888', flexShrink: 0 }}>
                    {chat.visitor_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{chat.visitor_name || 'Visitor'}</span>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        {tab === 'offline' && (
                          <span style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontSize: 9, padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>OFFLINE</span>
                        )}
                        {tab === 'live' && chat.status === 'pending' && (
                          <span style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontSize: 9, padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>PENDING</span>
                        )}
                        {chat.unread_count > 0 && tab === 'live' && (
                          <span style={{ background: '#00d55b', color: '#000', fontSize: 9, padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>{chat.unread_count}</span>
                        )}
                        {tab === 'history' && chat.rating && (
                          <span style={{ fontSize: 11, color: '#f59e0b' }}>{'★'.repeat(chat.rating)}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                      {tab === 'offline' ? `📴 ${chat.visitor_reason || 'Left offline message'}` : (chat.last_message || 'No messages')}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 3 }}>
                      <div style={{ fontSize: 10, color: '#333' }}>
                        {chat.city && `${chat.city}, `}{chat.country}
                        {tab === 'history' && chat.resolved_at && (
                          <span style={{ marginLeft: 4 }}>{new Date(chat.resolved_at).toLocaleDateString()}</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {tab === 'live' && chat.started_at && (
                          <span style={{ fontSize: 10, color: getWaitingTime(chat.started_at).includes('h') || (parseInt(getWaitingTime(chat.started_at)) > 5) ? '#ff4444' : '#888' }}>
                            ⏱ {getWaitingTime(chat.started_at)}
                          </span>
                        )}
                        {tab === 'live' && (
                          <button onClick={e => togglePin(chat.id, e)} title={pinnedChats.includes(chat.id) ? 'Unpin' : 'Pin'}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, opacity: pinnedChats.includes(chat.id) ? 1 : 0.25, padding: 0, lineHeight: 1 }}>
                            📌
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Chat window */}
        {selected ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '11px 18px', borderBottom: '1px solid #1e1e1e', background: '#0d0d0d', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#001a0d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#00d55b', flexShrink: 0 }}>
                {selected.visitor_name?.[0]?.toUpperCase() || '?'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{selected.visitor_name || 'Visitor'}</div>
                <div style={{ fontSize: 11, color: '#555' }}>{selected.visitor_email} · {selected.city}, {selected.country} · {selected.device_type}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {selected.status !== 'resolved' && (
                  <>

                {prechatAnswers.length > 0 && (
                      <button onClick={() => setShowPrechat(!showPrechat)}
                        style={{ padding: '6px 12px', background: showPrechat ? 'rgba(0,213,91,0.1)' : '#1a1a1a', border: `1px solid ${showPrechat ? '#00d55b' : '#1e1e1e'}`, color: showPrechat ? '#00d55b' : '#888', borderRadius: 7, fontSize: 12, cursor: 'pointer' }}>
                        📋 Answers ({prechatAnswers.length})
                      </button>
                    )}
                    <button onClick={() => setShowNote(!showNote)}
                      style={{ padding: '6px 12px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 7, fontSize: 12, cursor: 'pointer' }}>
                      📝 Note
                    </button>
                    <button onClick={() => setShowEndModal(true)}
                      style={{ padding: '6px 16px', background: '#1a0000', border: '1px solid #ff4444', color: '#ff4444', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      ✕ End Chat
                    </button>
                  </>
                )}
                {selected.status === 'resolved' && (
                  <span style={{ fontSize: 12, padding: '5px 12px', background: 'rgba(0,213,91,0.1)', color: '#00d55b', borderRadius: 7 }}>✓ Resolved</span>
                )}
              </div>
            </div>

            {/* Visitor info bar */}
            <div style={{ padding: '6px 18px', background: '#0a0a0a', borderBottom: '1px solid #1e1e1e', display: 'flex', gap: 18, flexWrap: 'wrap', flexShrink: 0 }}>
              {[['📧', selected.visitor_email], ['📱', selected.visitor_phone], ['🌐', selected.current_page], ['📍', `${selected.city || ''}${selected.country ? ', ' + selected.country : ''}`]]
                .filter(([, v]) => v).map(([icon, val]) => (
                  <span key={val} style={{ fontSize: 11, color: '#555', display: 'flex', gap: 4 }}><span>{icon}</span><span>{val}</span></span>
                ))}
            </div>

            {/* Pre-chat answers panel */}
            {showPrechat && prechatAnswers.length > 0 && (
              <div style={{ padding: '12px 18px', background: '#0a1a0d', borderBottom: '1px solid rgba(0,213,91,0.2)', flexShrink: 0 }}>
                <div style={{ fontSize: 12, color: '#00d55b', fontWeight: 600, marginBottom: 10 }}>📋 Pre-chat Answers</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                  {prechatAnswers.map((a, i) => (
                    <div key={i} style={{ background: '#111', borderRadius: 8, padding: '8px 12px' }}>
                      <div style={{ fontSize: 11, color: '#555', marginBottom: 3 }}>{a.question_text}</div>
                      <div style={{ fontSize: 13, color: '#fff', fontWeight: 500 }}>{a.answer}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Note input */}
            {showNote && (
              <div style={{ padding: '8px 16px', background: '#0a1a0d', borderBottom: '1px solid rgba(0,213,91,0.2)', display: 'flex', gap: 8, flexShrink: 0 }}>
                <input value={note} onChange={e => setNote(e.target.value)} placeholder="Internal note (not visible to visitor)..."
                  style={{ flex: 1, background: '#111', border: '1px solid #00d55b' }} />
                <button onClick={addNote} style={{ padding: '8px 16px', background: '#00d55b', color: '#000', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer', border: 'none' }}>Save</button>
              </div>
            )}

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#333', fontSize: 13, padding: 32 }}>No messages yet</div>
              ) : messages.map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: m.sender_type === 'agent' ? 'flex-end' : m.sender_type === 'system' ? 'center' : 'flex-start' }}>
                  {m.sender_type === 'system' ? (
                    <span style={{ fontSize: 11, color: '#333', background: '#111', padding: '3px 12px', borderRadius: 20, border: '1px solid #1e1e1e' }}>{m.message_text}</span>
                  ) : (
                    <div style={{ maxWidth: '70%' }}>
                      <div style={{ padding: '9px 13px', borderRadius: 12, background: m.sender_type === 'agent' ? '#00d55b' : '#1a1a1a', color: m.sender_type === 'agent' ? '#000' : '#fff', fontSize: 13, lineHeight: 1.5, borderBottomRightRadius: m.sender_type === 'agent' ? 2 : 12, borderBottomLeftRadius: m.sender_type === 'visitor' ? 2 : 12, fontWeight: m.sender_type === 'agent' ? 500 : 400 }}>
                        {m.message_text}
                      </div>
                      <div style={{ fontSize: 10, color: '#333', marginTop: 3, textAlign: m.sender_type === 'agent' ? 'right' : 'left' }}>
                        {m.sender_type === 'agent' ? (m.agent_name || user.name || 'You') : 'Visitor'} · {new Date(m.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {selected.status === 'resolved' && selected.rating && (
                <div style={{ marginTop: 10, background: '#111', border: '1px solid #1e1e1e', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Chat rating</div>
                  <div style={{ fontSize: 22, color: '#f59e0b' }}>{'★'.repeat(selected.rating)}{'☆'.repeat(5 - selected.rating)}</div>
                  {selected.rating_comment && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>"{selected.rating_comment}"</div>}
                </div>
              )}
              <div ref={msgEnd} />
            </div>

            {/* Message input */}
            {selected.status !== 'resolved' && (
              <div style={{ padding: '10px 14px', borderTop: '1px solid #1e1e1e', background: '#0d0d0d', flexShrink: 0 }}>
                {showCanned && (
                  <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, marginBottom: 8, maxHeight: 150, overflowY: 'auto' }}>
                    {cannedReplies.length === 0 ? (
                      <div style={{ padding: '10px 14px', fontSize: 12, color: '#555' }}>No canned replies yet</div>
                    ) : cannedReplies.filter(cr => {
                        if (msg.startsWith('/') && msg.length > 1) {
                          const q = msg.slice(1).toLowerCase();
                          return (cr.shortcut || '').toLowerCase().includes(q) || cr.title.toLowerCase().includes(q);
                        }
                        return true;
                      }).map(cr => (
                      <div key={cr.id} onClick={() => { setMsg(cr.message); setShowCanned(false); }}
                        style={{ padding: '8px 14px', fontSize: 13, color: '#888', cursor: 'pointer', borderBottom: '1px solid #0d0d0d' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#1a1a1a'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#888'; }}>
                        <strong style={{ color: '#00d55b' }}>/{cr.shortcut || cr.title}</strong> — {cr.message.substring(0, 55)}...
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button onClick={() => setShowCanned(!showCanned)}
                    style={{ padding: '0 10px', height: 38, background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#555', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}
                    title="Canned replies">⚡</button>
                  <input value={msg}
                    onChange={e => {
                    const val = e.target.value;
                    setMsg(val);
                    socket?.emit('typing:start', { chat_id: selected.id, sender_type: 'agent' });
                    // Show canned replies when typing /
                    if (val.startsWith('/')) setShowCanned(true);
                    else setShowCanned(false);
                  }}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMsg()}
                    placeholder="Type a message... (Enter to send)"
                    style={{ flex: 1 }} />
                  <button onClick={sendMsg}
                    style={{ padding: '0 18px', height: 38, background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none', flexShrink: 0 }}>
                    Send
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 48 }}>💬</div>
            <div style={{ fontSize: 16, fontWeight: 500, color: '#555' }}>Select a chat to start</div>
            <div style={{ fontSize: 13, color: '#333' }}>Go online to receive new chats</div>
          </div>
        )}
      </div>
    </DashLayout>
  );
}